/**
 * utils/connectionPool/acquire.ts — acquire/release methods for ConnectionPoolManager
 */

import { ConnectionPoolManager } from './manager.js';

declare module './manager.js' {
  interface ConnectionPoolManager {
    acquireConnection(): Promise<string>;
    releaseConnection(id: string): void;
  }
}

export function applyAcquireMethods() {
  Object.assign(ConnectionPoolManager.prototype, {
    /**
     * Acquire connection from pool
     */
    async acquireConnection(): Promise<string> {
        // Try to find available connection
        for (const [id, conn] of this.connections) {
            if (!conn.inUse) {
                conn.inUse = true;
                conn.lastUsed = Date.now();

                if (import.meta.env.DEV) {
                    console.log(`[Pool] Connection acquired: ${id}`);
                }

                return id;
            }
        }

        // Create new connection if available
        if (this.connections.size < this.config.maxConnections) {
            const id = this.createConnection();
            const conn = this.connections.get(id)!;
            conn.inUse = true;
            conn.lastUsed = Date.now();
            return id;
        }

        // Wait for connection to become available
        return new Promise((resolve, reject) => {
            const timeout = setTimeout(() => {
                reject(new Error('Failed to acquire connection within timeout'));
            }, this.config.acquireTimeout);

            this.acquireQueue.push({
                resolve: (id: string) => {
                    clearTimeout(timeout);
                    resolve(id);
                },
                reject: (err: Error) => {
                    clearTimeout(timeout);
                    reject(err);
                },
            });
        });
    },

    /**
     * Release connection back to pool
     */
    releaseConnection(id: string): void {
        const conn = this.connections.get(id);
        if (!conn) {
            console.warn(`[Pool] Connection not found: ${id}`);
            return;
        }

        conn.inUse = false;
        conn.lastUsed = Date.now();

        // Check if anyone is waiting for a connection
        const waiter = this.acquireQueue.shift();
        if (waiter) {
            conn.inUse = true;
            conn.lastUsed = Date.now();
            waiter.resolve(id);
        }

        if (import.meta.env.DEV) {
            console.log(`[Pool] Connection released: ${id}`);
        }
    },
  });
}
